/**
 * Formats the given value to a comma separated number
 * @param value The value to format
 */
export function formatCommas(value: number | string): string {
  const parts = value.toString().split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

/**
 * Checks if the value has only numbers
 * @param value The value to check
 */
export function areOnlyNumbers(value: string): boolean {
  const reg = /^\d+$/
  return reg.test(value)
}

/**
 * Strips a formatted number to only digits
 * @param formattedNum The formatted number to strip
 */
export function stripToNumbers(formattedNum: string): string {
  return formattedNum.replace(/\D/g, '')
}

/**
 * Rounds the given number to the desired number of decimal places
 * @param num The number to round
 * @param numDecimals The number of decimal places to round to
 */
export function roundNumber(num: number, numDecimals: number): number {
  const factor = Math.pow(10, numDecimals)
  return Math.round(num * factor) / factor
}

/**
 * Returns the dollar amount and the cents as strings
 * @param price
 */
export function getDollarsAndCentsFromPrice(price: number | string): DollarsAndCents {
  const splitPrice = price.toString().split('.')
  const dollars = splitPrice[0]

  let cents = '00'
  if (splitPrice.length === 2) {
    cents = splitPrice[1]
    if (cents.length === 1) {
      cents += '0'
    } else {
      cents = cents.substring(0, 2)
    }
  }

  return { dollars, cents }
}

/**
 * Gets a formatted price with 2 decimal places
 * @param price The price to format
 */
export function getFormattedPrice(price: number): string {
  const { dollars, cents } = getDollarsAndCentsFromPrice(roundNumber(price, 2))
  return formatCommas(`${dollars}.${cents}`)
}

const CURRENCIES = {
  usd: '$',
}

/**
 * Converts a price that is in cents to a formatted price with the given formatting options
 * @param priceInCents The price in cents
 * @param options Any additional formatting options
 */
export function formatPriceInCents(priceInCents: number, options: PriceFormatOptions = {}): string {
  const showDouble0ForCents = options.showDouble0ForCents || false
  let currency = options.currency ?? '$'
  if (CURRENCIES[currency]) {
    currency = CURRENCIES[currency]
  }

  let formattedPrice = getFormattedPrice(priceInCents / 100)

  // Remove the last 3 chars if it ends in ".00"
  if (!showDouble0ForCents && formattedPrice.slice(-2) === '00') {
    formattedPrice = formattedPrice.substring(0, formattedPrice.length - 3)
  }

  return `${currency}${formattedPrice}`
}

// Converts 1000s to Ks and 1000000s to Ms
export function abbreviateNumber(num: number): string {
  if (num < 1000) {
    return num.toString()
  }

  if (num >= 1000 && num < 1000000) {
    let thousands: number | RegExpMatchArray | null = num / 1000
    if (num < 10000) {
      thousands = thousands.toString().match(/^[0-9][.]?[0-9]?/)
    } else {
      thousands = thousands.toString().match(/^[0-9]+/)
    }
    return thousands + 'K'
  }

  let millions: number | RegExpMatchArray | null = num / 1000000
  if (num < 10000000) {
    millions = millions.toString().match(/^[0-9][.]?[0-9]?/)
  } else {
    millions = millions.toString().match(/^[0-9]+/)
  }
  return millions + 'M'
}

// region Types
export type DollarsAndCents = {
  dollars: string
  cents: string
}

type PriceFormatOptions = {
  currency?: string | null
  showDouble0ForCents?: boolean
}
// endregion Types
